import { useMemo, useState } from "react";
import axios from "axios";
import { PhoneCall, Truck, PackageCheck, Route, Flag, Clock, Loader2, ArrowRight } from "lucide-react";
import { Reveal, MonoLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING = 15;
const DISPATCH = 100;
const LOAD = 20;
const DELIVERY = 30;
const SPEED = 48;

const fmt = (mins) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const clockTime = (from, addMins) =>
  new Date(from.getTime() + addMins * 60000).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

const TransitCalculator = () => {
  const [pickupZip, setPickupZip] = useState("60601");
  const [deliveryZip, setDeliveryZip] = useState("37201");
  const [miles, setMiles] = useState(472);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const calc = useMemo(() => {
    const safeMiles = Math.max(0, Number(miles) || 0);
    const transit = Math.round((safeMiles / SPEED) * 60);
    const total = BOOKING + DISPATCH + LOAD + transit + DELIVERY;
    const now = new Date();
    return {
      transit,
      total,
      pickup: clockTime(now, BOOKING + DISPATCH + LOAD),
      delivered: clockTime(now, total),
    };
  }, [miles]);

  const estimate = async () => {
    setError("");
    if (!/^\d{5}$/.test(pickupZip) || !/^\d{5}$/.test(deliveryZip)) {
      setError("Enter both ZIP codes as 5 digits.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mileage`, {
        pickup_zip: pickupZip,
        delivery_zip: deliveryZip,
      });
      setMiles(Math.round(res.data.miles));
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Could not look up that route — type the miles directly below."
      );
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { icon: PhoneCall, label: "Book & dispatch confirmed", time: BOOKING },
    { icon: Truck, label: "Driver arrives at pickup", time: DISPATCH },
    { icon: PackageCheck, label: "Freight loaded & secured", time: LOAD },
    { icon: Route, label: `Transit at ${SPEED} mph average`, time: calc.transit },
    { icon: Flag, label: "Delivery & proof of delivery", time: DELIVERY },
  ];

  const zipCls =
    "w-full h-14 bg-transparent border border-white/20 px-4 font-mono text-xl font-bold text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-volt focus:border-volt tracking-widest";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <Reveal className="lg:col-span-5">
        <MonoLabel light>Ground freight — time estimator</MonoLabel>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.02]">
          How fast?
          <br />
          This fast.
        </h2>
        <p className="mt-6 text-white/60 leading-relaxed font-medium max-w-md">
          Enter your pickup and delivery ZIP codes for a real routed mileage and
          a live estimate of total booking and transit time — based on a
          dedicated sprinter van, up to 3 pallets and 3,000 lbs.
        </p>

        <div className="mt-10">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="pickup-zip" className="font-mono text-xs tracking-[0.2em] uppercase text-white/50">
                Pickup ZIP
              </label>
              <input
                id="pickup-zip"
                data-testid="calculator-pickup-zip"
                inputMode="numeric"
                maxLength={5}
                placeholder="60601"
                value={pickupZip}
                onChange={(e) => setPickupZip(e.target.value.replace(/\D/g, ""))}
                className={`${zipCls} mt-3`}
              />
            </div>
            <div>
              <label htmlFor="delivery-zip" className="font-mono text-xs tracking-[0.2em] uppercase text-white/50">
                Delivery ZIP
              </label>
              <input
                id="delivery-zip"
                data-testid="calculator-delivery-zip"
                inputMode="numeric"
                maxLength={5}
                placeholder="37201"
                value={deliveryZip}
                onChange={(e) => setDeliveryZip(e.target.value.replace(/\D/g, ""))}
                className={`${zipCls} mt-3`}
              />
            </div>
          </div>
          <button
            onClick={estimate}
            disabled={loading}
            data-testid="calculator-estimate-button"
            className="mt-4 flex items-center justify-center gap-3 w-full h-13 py-3.5 bg-volt text-navy-deep font-display text-base font-bold tracking-wide hover:bg-white disabled:opacity-60 transition-[background-color] duration-200"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <>CALCULATE ROUTE <ArrowRight size={16} /></>}
          </button>
          {error && (
            <p data-testid="calculator-error" className="mt-3 font-mono text-xs text-red-400 leading-relaxed">
              {error}
            </p>
          )}
          <div className="mt-5 flex items-center gap-3">
            <label htmlFor="miles-input" className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/40 shrink-0">
              Road miles
            </label>
            <input
              id="miles-input"
              data-testid="calculator-miles-input"
              type="number"
              min="0"
              max="4000"
              value={miles}
              onChange={(e) => setMiles(e.target.value)}
              className="w-28 h-10 bg-transparent border border-white/15 px-3 font-mono text-base font-bold text-white focus:outline-none focus:ring-2 focus:ring-volt focus:border-volt"
            />
            <span className="font-mono text-[11px] text-white/30">auto-filled, editable</span>
          </div>
        </div>

        <div className="mt-10 border border-volt/30 bg-volt/5 p-6">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-volt">
            Estimated total — booking to delivery
          </p>
          <p
            data-testid="calculator-total-time"
            className="mt-2 font-mono text-5xl sm:text-6xl font-bold text-white tracking-tight"
          >
            {fmt(calc.total)}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-4 font-mono text-sm">
            <div>
              <p className="text-white/40 text-[11px] tracking-widest uppercase">Wheels rolling</p>
              <p data-testid="calculator-pickup-time" className="text-white font-bold mt-1">
                ~{calc.pickup}
              </p>
            </div>
            <div>
              <p className="text-white/40 text-[11px] tracking-widest uppercase">Delivered by</p>
              <p data-testid="calculator-delivery-time" className="text-volt font-bold mt-1">
                ~{calc.delivered}
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15} className="lg:col-span-7">
        <ol className="relative border-l border-white/15 ml-3 space-y-0" data-testid="calculator-timeline">
          {steps.map((step, i) => (
            <li key={i} className="relative pl-10 py-6 group">
              <span className="absolute -left-[15px] top-6 flex h-[30px] w-[30px] items-center justify-center bg-navy border border-white/20 group-hover:border-volt transition-colors duration-300">
                <step.icon size={14} className="text-volt" />
              </span>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold text-white/85">
                  <span className="font-mono text-xs text-white/35 mr-3">0{i + 1}</span>
                  {step.label}
                </p>
                <p className="font-mono text-lg font-bold text-white">
                  {fmt(step.time)}
                </p>
              </div>
              <div className="mt-3 h-1 w-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-volt transition-[width] duration-500"
                  style={{
                    width: `${calc.total ? Math.max(3, (step.time / calc.total) * 100) : 3}%`,
                  }}
                />
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-6 pl-10 flex items-start gap-2 text-white/40 text-xs font-medium leading-relaxed">
          <Clock size={14} className="mt-px shrink-0" />
          Estimate only. Actual times vary with traffic, weather, route and
          carrier availability. Confirm exact routing with your Way2Go agent.
        </p>
      </Reveal>
    </div>
  );
};

export default TransitCalculator;
